import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import {issueAttributesType, locationsTreeNodesDictionaryType} from '../../constants/proptype-validation';
import cx from 'classnames';
import lodashGet from 'lodash/get';
import i18n from '../../utils/i18n';
import {toUtcLongDate, isLate} from '../../utils/date-helpers';
import StatusPill from '../common/StatusPill';
import InlineLoader from '@adsk/bim360-matrix-react-inline-loader';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';

export default class IssueSummary extends PureComponent {
  getLocationText() {
    const {issue, isLocationsTreeLoading, isLocationsLoadingError} = this.props;
    const {
      [ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS]: locationDetails,
      [ISSUE_ATTRIBUTE_NAMES.LOCATION_ID]: locationId
    } = issue;
    let locationName = '';
    if (locationId) {
      locationName = lodashGet(this.props, `locationsTreeNodesDictionary[${locationId}].name`);
      if (!locationName) {
        if (isLocationsTreeLoading) {
          return i18n.t('locations.loading');
        } else if (isLocationsLoadingError) {
          return i18n.t('locations.error');
        }
      }
    }
    return locationName || locationDetails || i18n.t('unspecifiedValue');
  }

  render() {
    const {issue, isCurrentRevision, issueStartingRevision} = this.props;
    const {
      id,
      [ISSUE_ATTRIBUTE_NAMES.ID]: displayId,
      [ISSUE_ATTRIBUTE_NAMES.STATUS]: status,
      [ISSUE_ATTRIBUTE_NAMES.TITLE]: title,
      [ISSUE_ATTRIBUTE_NAMES.DUE_DATE]: dueDate,
      [ISSUE_ATTRIBUTE_NAMES.CLOSED_AT]: closedAt,
      [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]: assignedTo
    } = issue;
    const dueDateClasses = cx('IssueSummary__due-date', {
      'IssueSummary__due-date--overdue': !closedAt && isLate(dueDate)
    });
    const assignToDisplay = this.props.getEntityDisplayNameById(
      assignedTo,
      i18n.t('activity.formerUser'),
      i18n.t('assignment.assigneeUnspecified')
    );
    const currentVersionI18n = isCurrentRevision ? 'version.currentIndicator' : 'version.notCurrentIndicator';
    const documentVersionClasses = cx('IssueSummary__document-version', {
      'IssueSummary__document-version--not-current': !isCurrentRevision
    });
    const locationValue = this.getLocationText();

    return (
      <div
        className="IssueSummary"
        onClick={() => this.props.changeLocationAction(`/${TYPE_QUALITY_ISSUES}/${id}`)}
        data-testid={`IssueSummary--${id}`}
      >
        {/*Header*/}
        <div className="IssueSummary__header-container">
          <StatusPill status={status} />
          <span className={dueDateClasses}>{dueDate ? toUtcLongDate(dueDate) : i18n.t('dueDate.unspecified')}</span>
        </div>

        {/*Title*/}
        <div className="IssueSummary__title" title={title}>
          <span className="IssueSummary__display-id">#{displayId}</span>
          <span className="IssueSummary__title-text"> - {title}</span>
        </div>

        {/*Assigned to*/}
        <div className="IssueSummary__general-info">
          <span>{i18n.t('assignment.to')}: </span>
          <span className="IssueSummary__general-info-ellipsis" title={assignToDisplay}>
            {assignToDisplay}
          </span>
        </div>

        {/*Location*/}
        <div className="IssueSummary__general-info">
          <span>{i18n.t('issueAttributes.location.label')}: </span>
          <span className="IssueSummary__general-info-ellipsis" title={locationValue}>
            {locationValue}
          </span>
        </div>

        <InlineLoader className="IssueSummary__version-loader" />

        {/*Document version*/}
        <div className={documentVersionClasses}>
          {!isNullOrUndefined(issueStartingRevision) ? (
            <span>{`${i18n.t('version.shorthand')}${issueStartingRevision} (${i18n.t(currentVersionI18n)})`}</span>
          ) : (
            <span>&shy;{/*Empty placeholder when we don't have version*/}</span>
          )}
        </div>
      </div>
    );
  }
}

IssueSummary.displayName = 'IssueSummary';

IssueSummary.propTypes = {
  issue: issueAttributesType.isRequired,
  issueStartingRevision: PropTypes.string,
  isCurrentRevision: PropTypes.bool,
  changeLocationAction: PropTypes.func.isRequired,
  isLocationsTreeLoading: PropTypes.bool,
  isLocationsLoadingError: PropTypes.bool,
  locationsTreeNodesDictionary: locationsTreeNodesDictionaryType,
  getEntityDisplayNameById: PropTypes.func.isRequired
};
